import { Checkbox, Input, Text } from "@clipboard-health/ui-components";
import { FormControlLabel, Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useRef } from "react";
import { Controller } from "react-hook-form";

import { OnboardingTitle } from "../../../SharedTitle";
import { type OnboardingStepProps } from "../../OnboardingStepWrapper";
import { useNameStep } from "../hooks/useNameStep";

export function NameStep(props: OnboardingStepProps) {
  const { onNext, worker } = props;

  const {
    control,
    handleSubmit,
    onNameChange,
    onNameBlur,
    noMiddleNameConfirmationChecked,
    onNoMiddleNameConfirmationChange,
    isSubmitting,
  } = useNameStep({
    onNext,
    worker,
  });

  const pageContentRef = useRef<HTMLDivElement>(null);
  const scrollInputBoxInView = (element: HTMLInputElement) => {
    if (pageContentRef.current) {
      const elementRect = element.getBoundingClientRect();
      const containerRect = pageContentRef.current.getBoundingClientRect();
      if (elementRect.bottom > containerRect.bottom || elementRect.top < containerRect.top) {
        element.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  };

  return (
    <>
      <PageContent ref={pageContentRef}>
        <Stack gap={4}>
          <OnboardingTitle text="Personal Information" />
          <Text semibold variant="body1">
            Enter your full legal name exactly as it is written on your valid government-issued ID
          </Text>
        </Stack>
        <Stack
          direction="column"
          justifyContent="space-between"
          sx={{
            height: "100%",
            marginTop: 6,
          }}
        >
          <Stack direction="column" spacing={6}>
            <Stack direction="column" spacing={3}>
              <Text semibold variant="body2" sx={{ paddingLeft: 1 }}>
                Legal First Name
              </Text>

              <Controller
                name="firstName"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      fullWidth
                      inputRef={field.ref}
                      type="text"
                      value={field.value}
                      error={Boolean(fieldState.error)}
                      disabled={isSubmitting}
                      inputProps={{
                        "aria-label": "Legal First Name",
                      }}
                      placeholder="Jane"
                      size="large"
                      onFocus={(event) => {
                        const element = event.target as HTMLInputElement;
                        scrollInputBoxInView(element);
                      }}
                      onChange={(event) => {
                        onNameChange(event.target.value, "firstName");
                      }}
                      onBlur={(event) => {
                        onNameBlur(event.target.value, "firstName");
                      }}
                    />

                    {fieldState.error && (
                      <Text
                        variant="caption"
                        color={(theme) => theme.palette.intent?.error.text}
                        sx={{ paddingLeft: 1 }}
                      >
                        {fieldState.error.message}
                      </Text>
                    )}
                  </>
                )}
              />
            </Stack>

            <Stack direction="column" spacing={3}>
              <Text semibold variant="body2" sx={{ paddingLeft: 1 }}>
                Legal Middle Name
              </Text>

              <Controller
                name="middleName"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      fullWidth
                      inputRef={field.ref}
                      type="text"
                      value={field.value}
                      error={Boolean(fieldState.error)}
                      inputProps={{
                        "aria-label": "Legal Middle Name",
                      }}
                      placeholder={noMiddleNameConfirmationChecked ? "" : "Doe"}
                      size="large"
                      disabled={noMiddleNameConfirmationChecked || isSubmitting}
                      onFocus={(event) => {
                        const element = event.target as HTMLInputElement;
                        scrollInputBoxInView(element);
                      }}
                      onChange={(event) => {
                        onNameChange(event.target.value, "middleName");
                      }}
                      onBlur={(event) => {
                        onNameBlur(event.target.value, "middleName");
                      }}
                    />

                    {fieldState.error && (
                      <Text
                        variant="caption"
                        color={(theme) => theme.palette.intent?.error.text}
                        sx={{ paddingLeft: 1 }}
                      >
                        {fieldState.error.message}
                      </Text>
                    )}
                  </>
                )}
              />

              <Controller
                name="noMiddleNameConfirmationChecked"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label={
                      <Text variant="body1" color="text.primary" ml={4}>
                        I confirm I do not have a middle name on my ID
                      </Text>
                    }
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(event) => {
                          onNoMiddleNameConfirmationChange(event.target.checked);
                        }}
                      />
                    }
                  />
                )}
              />
            </Stack>

            <Stack direction="column" spacing={3}>
              <Text semibold variant="body2" sx={{ paddingLeft: 1 }}>
                Legal Last Name
              </Text>

              <Controller
                name="lastName"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      fullWidth
                      inputRef={field.ref}
                      type="text"
                      value={field.value}
                      disabled={isSubmitting}
                      error={Boolean(fieldState.error)}
                      inputProps={{
                        "aria-label": "Legal Last Name",
                      }}
                      placeholder="Smith"
                      size="large"
                      onFocus={(event) => {
                        const element = event.target as HTMLInputElement;
                        scrollInputBoxInView(element);
                      }}
                      onChange={(event) => {
                        onNameChange(event.target.value, "lastName");
                      }}
                      onBlur={(event) => {
                        onNameBlur(event.target.value, "lastName");
                      }}
                    />

                    {fieldState.error && (
                      <Text
                        variant="caption"
                        color={(theme) => theme.palette.intent?.error.text}
                        sx={{ paddingLeft: 1 }}
                      >
                        {fieldState.error.message}
                      </Text>
                    )}
                  </>
                )}
              />
            </Stack>
          </Stack>
        </Stack>
      </PageContent>
      <PageFooterWrapper>
        <LoadingButton
          fullWidth
          size="large"
          isLoading={isSubmitting}
          type="submit"
          onClick={handleSubmit}
        >
          Continue
        </LoadingButton>
      </PageFooterWrapper>
    </>
  );
}
